import type { ModalResolverProps } from './modal.store'

import { lazy, Suspense } from 'react'
import { useSnapshot } from 'valtio'

import { modalActions, modalStore } from './modal.store'
import { ShortcutsModal } from '../shortcuts/ShortcutsModal'

// #region ============== LAZY COMPONENTS

const CellHistoryModal = lazy(() => import('./CellHistoryModal'))
const CellImageNodeModal = lazy(() => import('./CellImageNodeModal'))
const MentionsModal = lazy(() => import('../mentions/MentionsModal'))
const VariablesModal = lazy(() => import('../variables/VariablesModal'))

// #endregion

/**
 * Type-safe component that maps modals to their components
 */
function ModalResolver({ payload, onResolve }: ModalResolverProps) {
  return (
    <Suspense fallback={null}>
      {(() => {
        switch (payload.key) {
          case 'SHORTCUTS':
            return (
              <ShortcutsModal props={payload.props} onResolve={onResolve} />
            )

          case 'MENTIONS':
            return <MentionsModal props={payload.props} onResolve={onResolve} />

          case 'VARIABLES':
            return (
              <VariablesModal props={payload.props} onResolve={onResolve} />
            )

          case 'CELL_HISTORY':
            return (
              <CellHistoryModal props={payload.props} onResolve={onResolve} />
            )

          case 'CELL_IMAGENODE':
            return (
              <CellImageNodeModal props={payload.props} onResolve={onResolve} />
            )

          default: {
            const _exhaustive: never = payload
            return _exhaustive
          }
        }
      })()}
    </Suspense>
  )
}

/**
 * Headless component that manages and renders modals.
 */
export function ModalManager() {
  const snapshot = useSnapshot(modalStore)

  if (snapshot.stack.length === 0) return null

  return (
    <>
      {modalStore.stack.map((modal) => {
        return (
          <ModalResolver
            key={modal.id}
            payload={modal.payload}
            onResolve={(data) => modalActions.close(modal.id, data)}
          />
        )
      })}
    </>
  )
}
